import React, { useEffect, useRef, useCallback } from 'react';
import { Headphones } from 'lucide-react';

interface AnimatedHeaderProps {
  title: string;
  subtitle: string;
}

const AnimatedHeader: React.FC<AnimatedHeaderProps> = ({ title, subtitle }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  
  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      
      // Update canvas dimensions
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      
      // Reset transform and scale
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      
      // Update canvas style dimensions
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    }, 50); // Reduced debounce time for smoother updates
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Initial setup
    handleResize();
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Create ResizeObserver for the container
    const container = canvas.parentElement;
    if (container) {
      resizeObserverRef.current = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserverRef.current.observe(container);
    }
    
    // Wave parameters - adjust to fill the full height
    const waves = [
      { amplitude: 35, frequency: 0.015, speed: 0.02, color: '#5B6AFE', phase: 0 },
      { amplitude: 45, frequency: 0.008, speed: 0.01, color: '#8A6AFE', phase: Math.PI / 4 },
      { amplitude: 30, frequency: 0.02, speed: 0.015, color: '#6AC3FE', phase: Math.PI / 2 }
    ];

    // Variables to gradually change wave parameters
    let time = 0;
    const frequencyModulation = 0.0001;
    const amplitudeModulation = 0.05;
    
    // Animation function
    const animate = () => {
      if (!ctx || !canvas) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, 'rgba(91, 106, 254, 0.12)');
      gradient.addColorStop(0.5, 'rgba(138, 106, 254, 0.12)');
      gradient.addColorStop(1, 'rgba(106, 195, 254, 0.12)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Update time
      time += 0.01;
      
      // Draw each wave - spread across the full height
      waves.forEach((wave, index) => {
        // Gradually change frequency and amplitude
        wave.frequency += Math.sin(time) * frequencyModulation;
        wave.amplitude += Math.sin(time * 0.5) * amplitudeModulation;
        
        // Clamp amplitude to reasonable values based on canvas height
        wave.amplitude = Math.max(20, Math.min(canvas.height / 5, wave.amplitude));
        
        // Update phase
        wave.phase += wave.speed;
        
        // Begin drawing wave
        ctx.beginPath();
        
        // Create wave gradient
        const waveGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
        waveGradient.addColorStop(0, `${wave.color}80`);
        waveGradient.addColorStop(0.5, wave.color);
        waveGradient.addColorStop(1, `${wave.color}80`);
        
        ctx.strokeStyle = waveGradient;
        ctx.lineWidth = 4;
        
        // Draw wave line - position waves across full height
        const centerY = canvas.height * (0.2 + index * 0.3); // Distribute across the canvas height
        
        for (let x = 0; x < canvas.width; x++) {
          const y = Math.sin(x * wave.frequency + wave.phase) * wave.amplitude + centerY;
          
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.stroke();
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);
  
  return (
    <div className="relative overflow-hidden rounded-2xl shadow-lg mb-8">
      <canvas 
        ref={canvasRef}
        className="w-full h-60 rounded-2xl" 
        aria-hidden="true"
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="flex items-center gap-3 py-3 px-6 bg-background/70 backdrop-blur-md rounded-full shadow-md animate-pulse z-10">
          <Headphones className="text-primary animate-pulse" size={28} />
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-[#5B6AFE] via-[#8A6AFE] to-[#6AC3FE] text-transparent bg-clip-text">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedHeader;
